import React, { useCallback, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Box, Typography, Link, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, TableSortLabel } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';

import { PurchaseInterface, PurchaseStatuses } from '../types';
import { updatePurchase } from '../api/purchases';
import ImportOptionsDialog from './ImportOptionsDialog';

const statusDictionary: { [key in PurchaseStatuses]: string } = {
  approved: 'מאושר',
  pending: 'ממתין לאישור',
  canceled: 'בוטל'
}

interface PurchasesTableProps {
  purchases: PurchaseInterface[];
  loading: boolean;
  page: number;
  displayName: boolean;
  pageSize: number;
  total: number;
  onPageChange: (newPage: number) => void;
  onRefresh?: () => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (column: string) => void;
  onExport?: () => void;
  onImport?: (data: any[]) => Promise<void>;
  onDownloadTemplate?: () => void;
  title?: string;
  onContactSearch?: (contactId: string) => void;
}

function PurchasesTable({
  purchases,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
  onRefresh,
  displayName,
  sortBy,
  sortOrder,
  onSortChange,
  onExport,
  onImport,
  onDownloadTemplate,
  title,
  onContactSearch,
}: PurchasesTableProps): React.JSX.Element {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [importOptionsOpen, setImportOptionsOpen] = React.useState(false);
  const [editNotesModalOpen, setEditNotesModalOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState<PurchaseInterface | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);

  const [editReceiptModalOpen, setEditReceiptModalOpen] = useState(false);
  const [receiptUrlEdit, setReceiptUrlEdit] = useState('');
  const [savingReceipt, setSavingReceipt] = useState(false);

  const [editPaymentMethodModalOpen, setEditPaymentMethodModalOpen] = useState(false);
  const [paymentMethodEdit, setPaymentMethodEdit] = useState('');
  const [savingPaymentMethod, setSavingPaymentMethod] = useState(false);

  const totalPages = Math.ceil(total / pageSize);
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const handleEditNotes = (purchase: PurchaseInterface) => {
    setEditingPurchase(purchase);
    setNotes(purchase.notes || '');
    setEditNotesModalOpen(true);
  };

  const handleSaveNotes = async () => {
    if (!editingPurchase) return;
    setSaving(true);
    try {
      await updatePurchase(editingPurchase.id, { notes });
      setEditNotesModalOpen(false);
      setEditingPurchase(null);
      setNotes('');
      onRefresh?.();
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditNotesModalOpen(false);
    setEditingPurchase(null);
    setNotes('');
  };

  const handleEditReceipt = (purchase: PurchaseInterface) => {
    setEditingPurchase(purchase);
    setReceiptUrlEdit(purchase.receipt_url || '');
    setEditReceiptModalOpen(true);
  };

  const handleSaveReceipt = async () => {
    if (!editingPurchase) return;
    setSavingReceipt(true);
    try {
      await updatePurchase(editingPurchase.id, { receipt_url: receiptUrlEdit });
      setEditReceiptModalOpen(false);
      setEditingPurchase(null);
      setReceiptUrlEdit('');
      onRefresh?.();
    } finally {
      setSavingReceipt(false);
    }
  };

  const handleCloseEditReceiptModal = () => {
    setEditReceiptModalOpen(false);
    setEditingPurchase(null);
    setReceiptUrlEdit('');
  };

  const handleEditPaymentMethod = (purchase: PurchaseInterface) => {
    setEditingPurchase(purchase);
    setPaymentMethodEdit(purchase.payment_method || '');
    setEditPaymentMethodModalOpen(true);
  };

  const handleSavePaymentMethod = async () => {
    if (!editingPurchase) return;
    setSavingPaymentMethod(true);
    try {
      await updatePurchase(editingPurchase.id, { payment_method: paymentMethodEdit });
      setEditPaymentMethodModalOpen(false);
      setEditingPurchase(null);
      setPaymentMethodEdit('');
      onRefresh?.();
    } finally {
      setSavingPaymentMethod(false);
    }
  };

  const handleCloseEditPaymentMethodModal = () => {
    setEditPaymentMethodModalOpen(false);
    setEditingPurchase(null);
    setPaymentMethodEdit('');
  };

  const handleCancelPurchase = async (purchase: PurchaseInterface) => {
    try {
      await updatePurchase(purchase.id, { status: 'canceled' });
      window.open('https://app.greeninvoice.co.il/incomes', '_blank');
      onRefresh?.();
    } catch (error) {
      console.error('Error canceling purchase:', error);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const openReceipt = useCallback((url: string) => {
    setReceiptLoading(true);
    if (!url.includes('/api/')) {
      window.open(url, '_blank');
      setReceiptLoading(false);
      return
    }
    fetch(url)
      .then(res => res.blob())
      .then(blob => {
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, '_blank');
        setReceiptLoading(false);
      })
      .catch(() => setReceiptLoading(false));
  }, []);

  return (
    <>
      <TableContainer component={Paper} sx={{ overflowY: 'hidden', width: '100%' }}>
        {(title || onExport || onImport) && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
            <Typography variant="h6">{title || ''}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {onExport && (
                <IconButton
                  color="primary"
                  onClick={onExport}
                  title="ייצוא ל-CSV"
                  sx={{
                    backgroundColor: 'grey.300',
                    color: 'primary.main',
                    '&:hover': { backgroundColor: 'grey.400' }
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              )}
              {onImport && (
                <>
                  <input
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const Papa = await import('papaparse');
                      Papa.parse(file, {
                        header: true,
                        skipEmptyLines: true,
                        complete: async (results) => {
                          if (results.data) {
                            await onImport(results.data);
                          }
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }
                      });
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={() => {
                      if (onDownloadTemplate) {
                        setImportOptionsOpen(true);
                      } else {
                        fileInputRef.current?.click();
                      }
                    }}
                    title="ייבוא מ-CSV"
                    sx={{
                      backgroundColor: 'grey.300',
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'grey.400' }
                    }}
                  >
                    <UploadFileIcon />
                  </IconButton>
                  <ImportOptionsDialog
                    open={importOptionsOpen}
                    onClose={() => setImportOptionsOpen(false)}
                    onImportClick={() => fileInputRef.current?.click()}
                    onDownloadTemplateClick={() => onDownloadTemplate?.()}
                    title="אפשרויות ייבוא רכישות"
                  />
                </>
              )}
            </Box>
          </Box>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'id'}
                  direction={sortBy === 'id' ? sortOrder : 'asc'}
                  onClick={() => onSortChange?.('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              {displayName && (
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'contact_id'}
                    direction={sortBy === 'contact_id' ? sortOrder : 'asc'}
                    onClick={() => onSortChange?.('contact_id')}
                  >
                    שם
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'variant_name_raw'}
                  direction={sortBy === 'variant_name_raw' ? sortOrder : 'asc'}
                  onClick={() => onSortChange?.('variant_name_raw')}
                >
                  קורס
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'amount'}
                  direction={sortBy === 'amount' ? sortOrder : 'asc'}
                  onClick={() => onSortChange?.('amount')}
                >
                  סכום
                </TableSortLabel>
              </TableCell>
              <TableCell>קישור קבלה</TableCell>
              <TableCell>מספר חשבונית</TableCell>
              <TableCell>שולם ע״י</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'created_at'}
                  direction={sortBy === 'created_at' ? sortOrder : 'asc'}
                  onClick={() => onSortChange?.('created_at')}
                >
                  נוצר בתאריך
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'status'}
                  direction={sortBy === 'status' ? sortOrder : 'asc'}
                  onClick={() => onSortChange?.('status')}
                >
                  סטטוס
                </TableSortLabel>
              </TableCell>
              <TableCell>הערות</TableCell>
              <TableCell>פעולה</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={displayName ? 11 : 10} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : purchases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={displayName ? 11 : 10} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">לא נמצאו רכישות</Typography>
                </TableCell>
              </TableRow>
            ) : (
              purchases.map((purchase) => (
                <TableRow key={purchase.id}>
                  <TableCell>{purchase.id}</TableCell>
                  {displayName && (
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{purchase?.contact?.name || 'N/A'}</span>
                        {onContactSearch && purchase.contact_id && (
                          <IconButton
                            size="small"
                            onClick={() => onContactSearch(purchase.contact_id)}
                            title="חפש רכישות עבור איש קשר זה"
                          >
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>{purchase.variant_name_raw || 'N/A'}</TableCell>
                  <TableCell>{formatCurrency(purchase.amount || 0)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\S*)?$/.test(purchase.receipt_url) ? (
                        <Link sx={{ cursor: 'pointer' }} onClick={() => openReceipt(purchase.receipt_url)}>פתח קבלה</Link>
                      ) : (
                        <Typography variant="body2">{purchase.receipt_url || ''}</Typography>
                      )}
                      <IconButton size="small" onClick={() => handleEditReceipt(purchase)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>{purchase.morning_doc_id || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">{purchase.payment_method || '-'}</Typography>
                      <IconButton size="small" onClick={() => handleEditPaymentMethod(purchase)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(purchase.created_at)}</TableCell>
                  <TableCell>{statusDictionary[purchase.status]}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {purchase.notes || 'אין הערות'}
                      </Typography>
                      <IconButton size="small" onClick={() => handleEditNotes(purchase)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {purchase.status !== 'canceled' && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleCancelPurchase(purchase)}
                        title="בטל רכישה"
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              )
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination UI */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
        <Typography variant="body2">
          מציג {from}-{to} מתוך {total} רכישות
        </Typography>
        <Box>
          <IconButton onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
            {'<'}
          </IconButton>
          <Typography variant="body2" component="span" sx={{ mx: 1 }}>
            עמוד {page} מתוך {totalPages}
          </Typography>
          <IconButton onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
            {'>'}
          </IconButton>
        </Box>
      </Box>

      {/* Edit Notes Modal */}
      <Dialog open={editNotesModalOpen} onClose={handleCloseEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>ערוך הערות</DialogTitle>
        <DialogContent>
          <TextField
            label="הערות"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={4}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal}>ביטול</Button>
          <Button onClick={handleSaveNotes} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'שמור'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Receipt URL Modal */}
      <Dialog open={editReceiptModalOpen} onClose={handleCloseEditReceiptModal} maxWidth="sm" fullWidth>
        <DialogTitle>ערוך קישור קבלה</DialogTitle>
        <DialogContent>
          <TextField
            label="קישור קבלה"
            value={receiptUrlEdit}
            onChange={(e) => setReceiptUrlEdit(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditReceiptModal}>ביטול</Button>
          <Button onClick={handleSaveReceipt} variant="contained" disabled={savingReceipt}>
            {savingReceipt ? <CircularProgress size={20} /> : 'שמור'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment Method Modal */}
      <Dialog open={editPaymentMethodModalOpen} onClose={handleCloseEditPaymentMethodModal} maxWidth="sm" fullWidth>
        <DialogTitle>ערוך אמצעי תשלום</DialogTitle>
        <DialogContent>
          <TextField
            label="שולם ע״י"
            value={paymentMethodEdit}
            onChange={(e) => setPaymentMethodEdit(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditPaymentMethodModal}>ביטול</Button>
          <Button onClick={handleSavePaymentMethod} variant="contained" disabled={savingPaymentMethod}>
            {savingPaymentMethod ? <CircularProgress size={20} /> : 'שמור'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={receiptLoading} maxWidth="xs" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PurchasesTable; 